import { useEffect, useRef } from 'react';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NotificationsApiAdapter } from '../adapters/NotificationsApiAdapter';
import { useAuthStore } from '@shared/stores/authStore';

const POLLING_INTERVAL = 2 * 60 * 1000; // 2 minutes
const MAX_UNREAD_FETCH = 200; // Fetch enough to get accurate count

export const useUnreadCount = () => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new NotificationsApiAdapter(httpClient);
  const previousCount = useRef<number | null>(null);
  const user = useAuthStore((state) => state.user);
  const userId = user?.id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['notifications', 'unread-count', userId],
    queryFn: async () => {
      // API bug: returns all team users' notifications, need to fetch and filter client-side
      const response = await adapter.getNotifications({
        per_page: MAX_UNREAD_FETCH,
        filters: {
          logic: 'AND',
          filters: [{ column: 'read_at', operator: 'is_null' }],
        },
      });
      return response.data.filter((n) => {
        if (userId == null || n.user_id == null) return false;
        if (Number(n.user_id) !== Number(userId)) return false;
        return !n.archived_at;
      }).length;
    },
    refetchInterval: POLLING_INTERVAL,
    staleTime: 30 * 1000,
    enabled: !!userId,
  });

  const count = data ?? 0;

  // Invalidate notifications list when count changes
  useEffect(() => {
    if (previousCount.current !== null && previousCount.current !== count) {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    }
    previousCount.current = count;
  }, [count, queryClient]);

  return {
    count,
    isLoading,
    error,
    refetch,
  };
};
